import { useEffect, useState } from "react";
import {
  BookOpen,
  Users,
  FileText,
  BarChart3,
  TrendingUp,
  Sparkles,
  Plus,
  Settings,
  BookMarked,
  PenTool,
  Target,
  Presentation,
  Folder,
  GraduationCap,
} from "lucide-react";

import LogoCleanRebg from "../../assets/images/logo-clean-removebg.png";
import ProfileMenu from "../../components/layout/ProfileMenu";
import CreateLessonAI from "./components/CreateLessonAI";
import CreateSlidesAI from "./components/CreateSlidesAI";
import EvaluationActivityCreator from "./components/EvaluationCreator";
import SIGAAssistant from "./components/SIGAAssistant";
import ReportsManager from "./components/ReportsManager";
import SubjectsManager from "./components/SubjectsManager";
import StudentPerformanceManager from "../reports/components/StudentPerformanceManager";
import ClassesManager from "../turmas/components/ClassesManager";
import ActivitiesManager from "../activities/components/ActivitiesManager";
import FilesManager from "./components/FilesManager";
import SchoolProfileSwitcher from "./components/SchoolProfileSwitcher";
import SchoolProfileManager from "./components/SchoolProfileManager";
import { useAuth } from "../../app/AuthContext";
import { useSchoolProfile } from "../../app/SchoolProfileContext";
import { useTheme } from "../../app/ThemeContext";
import { useDashboardOverview } from "../../hooks/useDashboard";
import { useClasses } from "../../hooks/useClasses";
import { useEnrollments } from "../../hooks/useEnrollments";
import type { ClassData } from "../../lib/api";
import {
  StatCardSkeleton,
  ActivityCardSkeleton,
  ClassCardSkeleton,
} from "../../components/ui/Skeletons";

interface DashboardProps {
  onLogout?: () => void;
  onProfileClick?: () => void;
  onSettingsClick?: () => void;
  onClassesClick?: () => void;
  onActivitiesClick?: () => void;
}

export default function Dashboard({
  onLogout,
  onProfileClick,
  onSettingsClick,
  onClassesClick,
  onActivitiesClick,
}: DashboardProps) {
  const { user, token } = useAuth();
  const { theme } = useTheme();
  const { activeProfile } = useSchoolProfile();
  const [showProfileManager, setShowProfileManager] = useState(false);
  const [profileManagerInitialView, setProfileManagerInitialView] = useState<
    "list" | "form"
  >("list");
  const [showCreateSlides, setShowCreateSlides] = useState(false);
  const [showCreateLesson, setShowCreateLesson] = useState(false);
  const [showEvaluationCreator, setShowEvaluationCreator] = useState(false);
  const [showAICreator, setShowAICreator] = useState(false);
  const [activeTab, setActiveTab] = useState<
    | "overview"
    | "files"
    | "classes"
    | "activities"
    | "reports"
    | "performance"
    | "subjects"
  >("overview");
  const [selectedClassFilter, setSelectedClassFilter] = useState<string | null>(
    null,
  );
  const [customSubjects, setCustomSubjects] = useState<string[]>([]);

  // Use React Query hooks for data fetching with automatic caching
  const { data: overview, isLoading: overviewLoading } = useDashboardOverview(
    token,
    activeProfile?.id,
  );
  const { data: allClasses = [], isLoading: classesLoading } = useClasses(
    token,
    activeProfile?.id,
  );
  const { data: allEnrollments = [], isLoading: enrollmentsLoading } =
    useEnrollments(token, activeProfile?.id);

  const handleClassClick = (className: string) => {
    setSelectedClassFilter(className);
    setActiveTab("activities");
  };

  // Helper function to format due dates
  const formatDueLabel = (iso?: string) => {
    if (!iso) return "Sem prazo";
    const due = new Date(iso);
    if (Number.isNaN(due.getTime())) return "Sem prazo";
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const startOfDue = new Date(
      due.getFullYear(),
      due.getMonth(),
      due.getDate(),
    );
    const diffDays = Math.round(
      (startOfDue.getTime() - startOfToday.getTime()) / 86400000,
    );
    if (diffDays === 0) return "Hoje";
    if (diffDays === 1) return "Amanhã";
    if (diffDays < 0) return "Atrasado";
    return `${diffDays} dias`;
  };

  // Calculate unique students from enrollments for 100% realtime accuracy
  const uniqueStudentsCount = new Set(allEnrollments.map((e) => e.student_id))
    .size;

  // Extract overview data with defaults
  const overviewTotals = {
    students:
      enrollmentsLoading && !allEnrollments.length
        ? overview?.stats.students || 0
        : uniqueStudentsCount,
    classes:
      classesLoading && !allClasses.length
        ? overview?.stats.classes || 0
        : allClasses.length,
    activities: overview?.stats.activities ?? 0,
    // Use the RPC calculated rate directly, falling back to calculation if needed
    completionRatePct: overview?.stats.completion_rate ?? 0,
  };

  // Use allClasses for "Minhas Turmas" to ensure 100% realtime accuracy
  // but map them to the format expected by the dashboard UI
  const recentClasses = allClasses.slice(0, 4).map((c) => ({
    id: c.id,
    name: c.name,
    subject: c.subject || "Geral",
    students: allEnrollments.filter((e) => e.class_id === c.id).length,
    color: c.color || "var(--primary-color)",
  }));

  const recentActivities =
    overview?.recent_activities.map((a) => ({
      id: a.id,
      title: a.title,
      class: a.class_name,
      dueDate: formatDueLabel(a.due_date),
      status: a.status,
      submissions: a.submissions,
      total: a.total_students,
    })) ?? [];

  useEffect(() => {
    const saved = localStorage.getItem("custom_subjects");
    if (saved) {
      try {
        setCustomSubjects(JSON.parse(saved));
      } catch (e) {
        console.error(e);
      }
    }
  }, [activeTab]); // Intentionally refresh on tab change to keep data in sync

  const subjects = Array.from(
    new Set([
      ...allClasses.map((c) => c.subject).filter(Boolean),
      ...customSubjects,
    ]),
  ).sort() as string[];

  const stats = [
    {
      label: "Total de Alunos",
      value: String(overviewTotals.students),
      icon: Users,
      color: "#1967d2",
      bgColor: "#e8f0fe",
    },
    {
      label: "Turmas Ativas",
      value: String(overviewTotals.classes),
      icon: BookOpen,
      color: "#188038",
      bgColor: "#e6f4ea",
    },
    {
      label: "Atividades Criadas",
      value: String(overviewTotals.activities),
      icon: FileText,
      color: "#f9ab00",
      bgColor: "#fef7e0",
    },
    {
      label: "Taxa de Conclusão",
      value: `${overviewTotals.completionRatePct}%`,
      icon: TrendingUp,
      color: "#d93025",
      bgColor: "#fce8e6",
    },
  ];

  const aiSuggestions = [
    {
      title: "Criar slides sobre frações",
      description: "Apresentação interativa para 9º Ano A",
      icon: Presentation,
    },
    {
      title: "Criar plano de aula",
      description: "Baseado no desempenho da turma",
      icon: BookMarked,
    },
    {
      title: "SIGA Assistant",
      description: "Assistente inteligente para suas aulas",
      icon: Sparkles,
    },
    {
      title: "Análise de desempenho",
      description: "5 alunos precisam de atenção especial",
      icon: Target,
      action: () => setActiveTab("performance"),
    },
  ];

  const [initialSuggestionData, setInitialSuggestionData] = useState<any>(null);

  const handleSuggestionClick = (suggestion: any) => {
    const title = suggestion.title.toLowerCase();
    const description = suggestion.description;

    let topic = "";
    if (title.includes("sobre ")) {
      topic = title.split("sobre ")[1];
    } else if (title.includes("de ")) {
      topic = title.split("de ")[1];
    }

    if (title.includes("slides")) {
      setInitialSuggestionData({
        title: suggestion.title,
        topic: topic,
        // Subject intentionally left empty. Inference from description is unreliable here.
        subject: "",
      });
      setShowCreateSlides(true);
    } else if (title.includes("plano de aula")) {
      setInitialSuggestionData({
        title: `Aula sobre ${topic || "Novo Tópico"}`,
        topic: topic,
      });
      setShowCreateLesson(true);
    } else if (
      title.includes("exercícios") ||
      title.includes("atividade") ||
      title.includes("análise") ||
      title.includes("assistente") ||
      title.includes("assistant")
    ) {
      if (suggestion.action) {
        suggestion.action();
        return;
      }
      setInitialSuggestionData({
        title: suggestion.title,
        description: `Gere uma atividade baseada em: ${description}`,
      });
      setShowAICreator(true);
    }
  };

  return (
    <div className="min-h-screen bg-(--bg-secondary)">
      <header className="bg-(--header-bg) shadow-sm border-b border-(--border-color) sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <img
                src={LogoCleanRebg}
                alt="Logo"
                className="h-8 sm:h-20 w-auto object-contain shrink-0"
              />

              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl md:text-2xl font-black text-(--text-primary) leading-none tracking-tighter flex items-baseline gap-1">
                  SIGA
                  <span className="hidden sm:inline md:hidden text-sm font-medium text-(--text-tertiary) ml-1">
                    - Gestão Acadêmica
                  </span>
                  <span className="hidden md:inline text-sm md:text-lg font-medium text-(--text-tertiary) ml-1">
                    - Sistema Inteligente de Gestão Acadêmica
                  </span>
                </h1>
                <p className="sm:hidden text-[10px] uppercase tracking-wider font-bold text-(--text-tertiary) mt-0.5 whitespace-nowrap overflow-hidden text-ellipsis">
                  Gestão Acadêmica Inteligente
                </p>
                <p className="hidden sm:block md:hidden text-xs text-(--text-tertiary)">
                  Sistema acadêmico inteligente
                </p>
                <p className="hidden md:block text-xs text-(--text-tertiary)">
                  Dashboard Principal
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-4 shrink-0">
              <div className="hidden sm:block">
                <SchoolProfileSwitcher
                  onOpenManagement={() => {
                    setProfileManagerInitialView("list");
                    setShowProfileManager(true);
                  }}
                  onOpenAddForm={() => {
                    setProfileManagerInitialView("form");
                    setShowProfileManager(true);
                  }}
                />
              </div>

              <button
                className="hidden md:block p-2 hover:bg-(--bg-accent) rounded-lg transition"
                onClick={onSettingsClick}
              >
                <Settings className="w-5 h-5 text-(--text-tertiary)" />
              </button>

              <div className="hidden md:block h-8 w-px bg-(--border-color)"></div>

              <ProfileMenu
                onLogout={onLogout}
                onProfileClick={onProfileClick}
                onSettingsClick={onSettingsClick}
                onSchoolProfileClick={() => {
                  setProfileManagerInitialView("list");
                  setShowProfileManager(true);
                }}
              />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2 text-(--text-primary)">
            Olá, {user?.name || "Professor"}!
          </h2>
          <p className="text-lg text-(--text-tertiary)">
            Aqui está um resumo das suas atividades.
          </p>
        </div>

        <div className="mb-8 border-b border-(--border-color)">
          <div className="flex gap-6 overflow-x-auto">
            <button
              onClick={() => setActiveTab("overview")}
              className={`pb-4 px-2 font-medium transition whitespace-nowrap ${
                activeTab === "overview"
                  ? "border-b-2"
                  : "text-(--text-tertiary) hover:text-(--text-secondary)"
              }`}
              style={{
                borderColor:
                  activeTab === "overview"
                    ? "var(--primary-color)"
                    : "transparent",
                color:
                  activeTab === "overview"
                    ? "var(--primary-color)"
                    : "var(--text-tertiary)",
              }}
            >
              Visão Geral
            </button>
            <button
              onClick={() => setActiveTab("files")}
              className={`pb-4 px-2 font-medium transition whitespace-nowrap flex items-center gap-2 ${
                activeTab === "files"
                  ? "border-b-2"
                  : "text-(--text-tertiary) hover:text-(--text-secondary)"
              }`}
              style={{
                borderColor:
                  activeTab === "files"
                    ? "var(--primary-color)"
                    : "transparent",
                color:
                  activeTab === "files"
                    ? "var(--primary-color)"
                    : "var(--text-tertiary)",
              }}
            >
              <Folder className="w-4 h-4" />
              Meus Arquivos
            </button>
            <button
              onClick={() => setActiveTab("classes")}
              className={`pb-4 px-2 font-medium transition whitespace-nowrap flex items-center gap-2 ${
                activeTab === "classes"
                  ? "border-b-2"
                  : "text-(--text-tertiary) hover:text-(--text-secondary)"
              }`}
              style={{
                borderColor:
                  activeTab === "classes"
                    ? "var(--primary-color)"
                    : "transparent",
                color:
                  activeTab === "classes"
                    ? "var(--primary-color)"
                    : "var(--text-tertiary)",
              }}
            >
              <BookOpen className="w-4 h-4" />
              Gerenciar Turmas
            </button>
            <button
              onClick={() => setActiveTab("activities")}
              className={`pb-4 px-2 font-medium transition whitespace-nowrap flex items-center gap-2 ${
                activeTab === "activities"
                  ? "border-b-2"
                  : "text-(--text-tertiary) hover:text-(--text-secondary)"
              }`}
              style={{
                borderColor:
                  activeTab === "activities"
                    ? "var(--primary-color)"
                    : "transparent",
                color:
                  activeTab === "activities"
                    ? "var(--primary-color)"
                    : "var(--text-tertiary)",
              }}
            >
              <FileText className="w-4 h-4" />
              Gerenciar Atividades
            </button>
            <button
              onClick={() => setActiveTab("subjects")}
              className={`pb-4 px-2 font-medium transition whitespace-nowrap flex items-center gap-2 ${
                activeTab === "subjects"
                  ? "border-b-2"
                  : "text-(--text-tertiary) hover:text-(--text-secondary)"
              }`}
              style={{
                borderColor:
                  activeTab === "subjects"
                    ? "var(--primary-color)"
                    : "transparent",
                color:
                  activeTab === "subjects"
                    ? "var(--primary-color)"
                    : "var(--text-tertiary)",
              }}
            >
              <BookMarked className="w-4 h-4" />
              Gerenciar Matérias
            </button>
            <button
              onClick={() => setActiveTab("reports")}
              className={`pb-4 px-2 font-medium transition whitespace-nowrap flex items-center gap-2 ${
                activeTab === "reports"
                  ? "border-b-2"
                  : "hover:text-(--text-secondary)"
              }`}
              style={{
                borderColor:
                  activeTab === "reports"
                    ? "var(--primary-color)"
                    : "transparent",
                color:
                  activeTab === "reports"
                    ? "var(--primary-color)"
                    : "var(--text-tertiary)",
              }}
            >
              <BarChart3 className="w-4 h-4" />
              Ver Relatórios
            </button>
            <button
              onClick={() => setActiveTab("performance")}
              className={`pb-4 px-2 font-medium transition whitespace-nowrap flex items-center gap-2 ${
                activeTab === "performance"
                  ? "border-b-2"
                  : "text-(--text-tertiary) hover:text-(--text-secondary)"
              }`}
              style={{
                borderColor:
                  activeTab === "performance"
                    ? "var(--primary-color)"
                    : "transparent",
                color:
                  activeTab === "performance"
                    ? "var(--primary-color)"
                    : "var(--text-tertiary)",
              }}
            >
              <TrendingUp className="w-4 h-4" />
              Desempenho
            </button>
          </div>
        </div>

        <div style={{ display: activeTab === "overview" ? "block" : "none" }}>
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {overviewLoading ? (
                <>
                  <StatCardSkeleton />
                  <StatCardSkeleton />
                  <StatCardSkeleton />
                  <StatCardSkeleton />
                </>
              ) : (
                stats.map((stat, index) => (
                  <div
                    key={index}
                    className="bg-(--card-bg) rounded-xl p-6 shadow-sm border border-(--border-color) hover:shadow-md transition"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: stat.bgColor }}
                      >
                        <stat.icon
                          className="w-6 h-6"
                          style={{ color: stat.color }}
                        />
                      </div>
                    </div>
                    <p className="text-sm mb-1 text-(--text-tertiary)">
                      {stat.label}
                    </p>
                    <p className="text-3xl font-bold text-(--text-primary)">
                      {stat.value}
                    </p>
                  </div>
                ))
              )}
            </div>

            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-1">
                <div className="bg-(--card-bg) rounded-xl p-6 shadow-sm border border-(--border-color)">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles
                      className="w-5 h-5"
                      style={{ color: "var(--primary-color)" }}
                    />
                    <h3 className="text-lg font-semibold text-(--text-primary)">
                      Sugestões IA
                    </h3>
                  </div>
                  <div className="space-y-4">
                    {aiSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="p-4 rounded-lg hover:shadow-sm transition cursor-pointer bg-(--bg-secondary) hover:bg-(--bg-accent)"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-(--bg-accent)">
                            <suggestion.icon className="w-5 h-5 text-(--primary-color)" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm mb-1 text-(--text-primary)">
                              {suggestion.title}
                            </h4>
                            <p className="text-xs text-(--text-tertiary)">
                              {suggestion.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="bg-(--card-bg) rounded-xl p-6 shadow-sm border border-(--border-color)">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-(--text-primary)">
                      Atividades Recentes
                    </h3>
                    <button
                      onClick={() => setShowEvaluationCreator(true)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition hover:shadow-md bg-(--primary-color)"
                    >
                      <Plus className="w-4 h-4" />
                      Nova Atividade
                    </button>
                  </div>
                  <div className="space-y-4">
                    {overviewLoading ? (
                      <>
                        <ActivityCardSkeleton />
                        <ActivityCardSkeleton />
                        <ActivityCardSkeleton />
                      </>
                    ) : recentActivities.length === 0 ? (
                      <div className="text-center py-8 text-(--text-tertiary)">
                        Nenhuma atividade recente
                      </div>
                    ) : (
                      recentActivities.map((activity, index) => (
                        <div
                          key={index}
                          className="p-4 rounded-lg border border-(--border-color) hover:shadow-sm transition"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4
                                className="font-semibold mb-1"
                                style={{ color: "var(--text-primary)" }}
                              >
                                {activity.title}
                              </h4>
                              <p className="text-sm text-(--text-tertiary)">
                                {activity.class}
                              </p>
                            </div>
                            <span
                              className="px-3 py-1 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor:
                                  activity.status === "pending"
                                    ? "#fef7e0"
                                    : "#e6f4ea",
                                color:
                                  activity.status === "pending"
                                    ? "#f9ab00"
                                    : "#188038",
                              }}
                            >
                              Entrega: {activity.dueDate}
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${
                                    (activity.submissions / activity.total) *
                                    100
                                  }%`,
                                  backgroundColor: "var(--primary-color)",
                                }}
                              ></div>
                            </div>
                            <span
                              className="text-sm font-medium whitespace-nowrap"
                              style={{ color: "var(--text-tertiary)" }}
                            >
                              {activity.submissions}/{activity.total}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-(--card-bg) rounded-xl p-6 shadow-sm border border-(--border-color)">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-(--text-primary)">
                  Minhas Turmas
                </h3>
                <button
                  onClick={() => setActiveTab("classes")}
                  className="text-sm font-medium hover:opacity-75 transition"
                  style={{ color: "var(--primary-color)" }}
                >
                  Ver todas
                </button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {overviewLoading ? (
                  <>
                    <ClassCardSkeleton />
                    <ClassCardSkeleton />
                    <ClassCardSkeleton />
                    <ClassCardSkeleton />
                  </>
                ) : recentClasses.length === 0 ? (
                  <div className="col-span-full text-center py-8 text-(--text-tertiary)">
                    Nenhuma turma cadastrada
                  </div>
                ) : (
                  recentClasses.map((classItem, index) => (
                    <div
                      key={index}
                      onClick={() => handleClassClick(classItem.name)}
                      className="p-5 rounded-xl cursor-pointer hover:shadow-md transition border-2"
                      style={{
                        borderColor: classItem.color,
                        backgroundColor:
                          "color-mix(in srgb, var(--card-bg), transparent 90%)",
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: classItem.color }}
                        >
                          <BookOpen className="w-5 h-5 text-white" />
                        </div>
                        <span
                          className="text-xs font-medium px-2 py-1 rounded-full bg-(--card-bg)"
                          style={{ color: classItem.color }}
                        >
                          {classItem.students} alunos
                        </span>
                      </div>
                      <h4 className="font-bold text-lg mb-1 text-(--text-primary)">
                        {classItem.name}
                      </h4>
                      <p className="text-sm text-(--text-tertiary)">
                        {classItem.subject}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              <button
                onClick={() => setShowCreateSlides(true)}
                className="p-6 bg-(--card-bg) rounded-xl border-2 border-dashed hover:shadow-md transition text-left h-full"
                style={{ borderColor: "var(--border-color)" }}
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-3"
                  style={{ backgroundColor: "rgba(147, 52, 230, 0.1)" }}
                >
                  <Presentation
                    className="w-6 h-6"
                    style={{ color: "var(--purple-color)" }}
                  />
                </div>
                <h4
                  className="font-semibold mb-1"
                  style={{ color: "var(--text-primary)" }}
                >
                  Criar Slides com IA
                </h4>
                <p
                  className="text-sm"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  Apresentações profissionais em minutos
                </p>
              </button>

              <button
                onClick={() => setShowCreateLesson(true)}
                className="p-6 bg-(--card-bg) rounded-xl border-2 border-dashed border-(--border-color) hover:shadow-md transition text-left h-full"
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-3"
                  style={{ backgroundColor: "rgba(25, 103, 210, 0.1)" }}
                >
                  <Plus
                    className="w-6 h-6"
                    style={{ color: "var(--primary-color)" }}
                  />
                </div>
                <h4 className="font-semibold mb-1 text-(--text-primary)">
                  Criar Plano de Aula
                </h4>
                <p className="text-sm text-(--text-tertiary)">
                  Use IA para gerar planos personalizados
                </p>
              </button>

              <button
                onClick={() => setShowEvaluationCreator(true)}
                className="p-6 bg-(--card-bg) rounded-xl border-2 border-dashed border-(--border-color) hover:shadow-md transition text-left h-full"
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-3"
                  style={{ backgroundColor: "rgba(79, 70, 229, 0.1)" }}
                >
                  <GraduationCap
                    className="w-6 h-6"
                    style={{ color: "#4f46e5" }}
                  />
                </div>
                <h4 className="font-semibold mb-1 text-(--text-primary)">
                  Criar Atividades e Avaliações
                </h4>
                <p className="text-sm text-(--text-tertiary)">
                  Gerar provas e exercícios completos
                </p>
              </button>

              <button
                onClick={() => setShowAICreator(true)}
                className="p-6 bg-(--card-bg) rounded-xl border-2 border-dashed border-(--border-color) hover:shadow-md transition text-left h-full"
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-3"
                  style={{ backgroundColor: "rgba(234, 88, 12, 0.1)" }}
                >
                  <Sparkles className="w-6 h-6" style={{ color: "#ea580c" }} />
                </div>
                <h4 className="font-semibold mb-1 text-(--text-primary)">
                  SIGA Assistant
                </h4>
                <p className="text-sm text-(--text-tertiary)">
                  Assistente inteligente para suas aulas
                </p>
              </button>

              <button
                onClick={() => setActiveTab("performance")}
                className="p-6 bg-(--card-bg) rounded-xl border-2 border-dashed border-(--border-color) hover:shadow-md transition text-left h-full"
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-3"
                  style={{ backgroundColor: "rgba(16, 185, 129, 0.1)" }}
                >
                  <Users className="w-6 h-6" style={{ color: "#10b981" }} />
                </div>
                <h4 className="font-semibold mb-1 text-(--text-primary)">
                  Situação dos Alunos
                </h4>
                <p className="text-sm text-(--text-tertiary)">
                  Análise detalhada de desempenho
                </p>
              </button>
            </div>
          </>
        </div>

        <div style={{ display: activeTab === "classes" ? "block" : "none" }}>
          <ClassesManager
            onClassClick={handleClassClick}
            classes={allClasses}
          />
        </div>

        <div style={{ display: activeTab === "activities" ? "block" : "none" }}>
          <ActivitiesManager
            initialFilter={selectedClassFilter}
            classes={allClasses}
          />
        </div>

        <div style={{ display: activeTab === "reports" ? "block" : "none" }}>
          <ReportsManager mode="tab" />
        </div>

        <div
          style={{ display: activeTab === "performance" ? "block" : "none" }}
        >
          <StudentPerformanceManager />
        </div>

        <div style={{ display: activeTab === "files" ? "block" : "none" }}>
          <FilesManager />
        </div>

        <div style={{ display: activeTab === "subjects" ? "block" : "none" }}>
          <SubjectsManager classes={allClasses} />
        </div>
      </main>

      {showEvaluationCreator && (
        <EvaluationActivityCreator
          onClose={() => setShowEvaluationCreator(false)}
        />
      )}

      {showAICreator && (
        <SIGAAssistant
          onClose={() => {
            setShowAICreator(false);
            setInitialSuggestionData(null);
          }}
          apiBaseUrl="http://localhost:8000"
          apiToken={token}
          geminiApiKey={
            (typeof process !== "undefined"
              ? process.env.BUN_PUBLIC_GEMINI_API_KEY
              : "") || ""
          }
        />
      )}

      {showCreateSlides && (
        <CreateSlidesAI
          classes={allClasses.map((c) => c.name)}
          subjects={subjects}
          initialData={initialSuggestionData}
          onClose={() => {
            setShowCreateSlides(false);
            setInitialSuggestionData(null);
          }}
          onSave={(slides) => {
            console.log("Slides salvos:", slides);
            setShowCreateSlides(false);
            setInitialSuggestionData(null);
          }}
        />
      )}

      <CreateLessonAI
        isOpen={showCreateLesson}
        onClose={() => {
          setShowCreateLesson(false);
          setInitialSuggestionData(null);
        }}
        subjects={subjects}
        classes={allClasses.map((c) => c.name)}
        onLessonGenerated={(lesson) => {
          console.log("Aula gerada:", lesson);
        }}
      />
      <SchoolProfileManager
        isOpen={showProfileManager}
        onClose={() => setShowProfileManager(false)}
        initialView={profileManagerInitialView}
      />
    </div>
  );
}
